import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  TextField,
  Typography,
  Divider,
  FormHelperText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';

import { Question, QuestionOption } from './QuestionCard';
import { AiActions } from '../../../Services/_actions';
import ModalForms from '../../Shared/Modals/index.js';

interface EditQuestionModalProps {
  open: boolean;
  question: Question;
  onClose: () => void;
  onSave: (updated: Question) => void;
  knowledgeMapData?: any[];
  mode?: 'edit' | 'create';
  selectedBoard?: string;
  selectedClass?: string;
  selectedSubject?: string;
}

const normalizeOptions = (
  options?: QuestionOption[] | string[],
  topLevelReasoning?: string,
  topLevelIncorrectReasoning?: string
): QuestionOption[] => {
  if (!options || !Array.isArray(options) || options.length === 0) {
    return [{ text: '', is_correct: true, correct_reason: topLevelReasoning || '' }];
  }
  const normalized = options.map((o) =>
    typeof o === 'string'
      ? { text: o, is_correct: false, correct_reason: '', incorrect_reason: '' }
      : { correct_reason: '', incorrect_reason: '', ...o }
  );
  const hasPerOptionCorrectReason = normalized.some((o) => o.is_correct && o.correct_reason);
  if (!hasPerOptionCorrectReason && topLevelReasoning) {
    const correctIdx = normalized.findIndex((o) => o.is_correct);
    if (correctIdx !== -1) {
      normalized[correctIdx] = { ...normalized[correctIdx], correct_reason: topLevelReasoning };
    }
  }
  return normalized;
};

const flattenPrerequisites = (nodes: any[]): { id: string; name: string }[] => {
  const result: { id: string; name: string }[] = [];
  const walk = (list: any[]) => {
    (list || []).forEach((node) => {
      if (node?.id && node?.name) result.push({ id: node.id, name: node.name });
      if (node?.children?.length) walk(node.children);
    });
  };
  walk(nodes);
  return result;
};

const DIFFICULTY_OPTIONS = ['easy', 'medium', 'hard'];
const CHIP_COLORS = [
  '#2196F3', '#9C27B0', '#4CAF50', '#FF9800',
  '#E91E63', '#009688', '#FF5722', '#607D8B',
];
const PREREQ_COLORS = [
  '#5C6BC0', '#26A69A', '#EF5350', '#AB47BC',
  '#FFA726', '#42A5F5', '#66BB6A', '#EC407A',
];

type QuestionType = { id: string; label: string; icon: React.ReactNode; description: string };

const questionTypes: QuestionType[] = [
  { id: 'mcq', label: 'MCQ', icon: <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />, description: 'Multiple Choice Questions' },
  { id: 'multiple-selection', label: 'Multiple Selection', icon: <CheckBoxIcon sx={{ fontSize: 18 }} />, description: 'Select Multiple Answers' },
  { id: 'boolean', label: 'Boolean', icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, description: 'True or False' },
];

const normalizeQuestionType = (value?: string): string => {
  const type = (value || 'mcq').trim().toLowerCase();
  return questionTypes.some((qt) => qt.id === type) ? type : 'mcq';
};

const getDefaultOptionsForType = (type: string): QuestionOption[] => {
  if (type === 'boolean') {
    return [
      { text: 'True', is_correct: true, correct_reason: '' },
      { text: 'False', is_correct: false, incorrect_reason: '' },
    ];
  }
  return [
    { text: '', is_correct: true, correct_reason: '' },
    { text: '', is_correct: false, incorrect_reason: '' },
    { text: '', is_correct: false, incorrect_reason: '' },
    { text: '', is_correct: false, incorrect_reason: '' },
  ];
};

interface FieldErrors {
  board?: string;
  cls?: string;
  subject?: string;
  questionText?: string;
  topic?: string;
  subtopic?: string;
  difficulty?: string;
  questionType?: string;
  options?: string;
  correctOption?: string;
  correctReason?: string;
  incorrectReasons?: string;
}

const EditQuestionModal: React.FC<EditQuestionModalProps> = ({
  open,
  question,
  onClose,
  onSave,
  knowledgeMapData = [],
  mode = 'edit',
  selectedBoard: propBoard,
  selectedClass: propClass,
  selectedSubject: propSubject,
}) => {
  const isCreateMode = mode === 'create';

  const [questionText, setQuestionText] = useState('');
  const [options, setOptions] = useState<QuestionOption[]>([]);
  const [topic, setTopic] = useState('');
  const [subtopic, setSubtopic] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [questionType, setQuestionType] = useState('MCQ');
  const [topLevelIncorrectReasoning, setTopLevelIncorrectReasoning] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [selectedPrerequisites, setSelectedPrerequisites] = useState<string[]>([]);

  const [board, setBoard] = useState('');
  const [cls, setCls] = useState('');
  const [subject, setSubject] = useState('');

  const actions = AiActions();

  // ── Derived lists — same logic for both create and edit ──────────────────

  const allBoards: string[] = useMemo(() =>
    Array.from(new Set(knowledgeMapData.map((i: any) => i.board).filter(Boolean))),
    [knowledgeMapData]
  );

  const allClasses: string[] = useMemo(() => {
    if (!board) return [];
    return Array.from(new Set(
      knowledgeMapData
        .filter((i: any) => i.board === board)
        .map((i: any) => i.class)
        .filter(Boolean)
    ));
  }, [knowledgeMapData, board]);

  const allSubjects: string[] = useMemo(() => {
    if (!board || !cls) return [];
    return Array.from(new Set(
      knowledgeMapData
        .filter((i: any) => i.board === board && i.class === cls)
        .map((i: any) => i.subject)
        .filter(Boolean)
    ));
  }, [knowledgeMapData, board, cls]);

  // Topics — filtered by board+class+subject for BOTH modes
  const allTopics: string[] = useMemo(() => {
    const sourceData = (board || cls || subject)
      ? knowledgeMapData.filter((i: any) =>
          (!board   || i.board   === board) &&
          (!cls     || i.class   === cls) &&
          (!subject || i.subject === subject)
        )
      : knowledgeMapData;

    const topicSet = new Set<string>();
    sourceData.forEach((entry: any) => {
      (entry.topics || []).forEach((t: any) => {
        if (t.topic) topicSet.add(t.topic);
      });
    });
    return Array.from(topicSet);
  }, [knowledgeMapData, board, cls, subject]);

  // Subtopics — filtered by board+class+subject+topic for BOTH modes
  const subtopicOptions: string[] = useMemo(() => {
    if (!topic) return [];
    const subtopicSet = new Set<string>();
    knowledgeMapData.forEach((entry: any) => {
      if (board   && entry.board   !== board)   return;
      if (cls     && entry.class   !== cls)     return;
      if (subject && entry.subject !== subject) return;
      (entry.topics || []).forEach((t: any) => {
        if (t.topic !== topic) return;
        (t.subtopics || []).forEach((s: any) => {
          const name = typeof s === 'string' ? s : s?.subtopic || s?.name || '';
          if (name) subtopicSet.add(name);
        });
      });
    });
    return Array.from(subtopicSet);
  }, [knowledgeMapData, topic, board, cls, subject]);

  // Prerequisites — filtered by board+class+subject+topic+subtopic for BOTH modes
  const prerequisiteOptions: { id: string; name: string }[] = useMemo(() => {
    if (!subtopic) return [];
    const allPrereqs: { id: string; name: string }[] = [];
    knowledgeMapData.forEach((entry: any) => {
      if (board   && entry.board   !== board)   return;
      if (cls     && entry.class   !== cls)     return;
      if (subject && entry.subject !== subject) return;
      (entry.topics || []).forEach((t: any) => {
        if (topic && t.topic !== topic) return;
        (t.subtopics || []).forEach((s: any) => {
          const subtopicName = typeof s === 'string' ? s : s?.subtopic || s?.name || '';
          if (subtopicName !== subtopic) return;
          allPrereqs.push(...flattenPrerequisites(s.prerequisites || []));
        });
      });
    });
    const seen = new Set<string>();
    return allPrereqs.filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [knowledgeMapData, board, cls, subject, topic, subtopic]);

  // ── Init ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (open) {
      setQuestionText(question.question_text || question.question || '');
      setOptions(normalizeOptions(
        question.options as QuestionOption[],
        question.reasoning,
        question.incorrect_reasoning
      ));
      setTopic(question.topic || '');
      setSubtopic(question.subtopic || '');
      setSelectedPrerequisites(
        (question.prerequisites || [])
          .map((p: any) => typeof p === 'string' ? p : p?.name || p?.id || '')
          .filter(Boolean)
      );
      setDifficulty(
        (question.difficulty || 'medium').replace(/[\[\]'"]/g, '').trim().toLowerCase()
      );
      const normalizedQuestionType = normalizeQuestionType(question.question_type);
      setQuestionType(normalizedQuestionType);
      setOptions((prevOptions) => {
        if (normalizedQuestionType !== 'boolean') return prevOptions;
        const existingCorrectText = prevOptions.find((opt) => opt.is_correct)?.text?.trim().toLowerCase();
        return [
          {
            text: 'True',
            is_correct: existingCorrectText !== 'false',
            correct_reason: existingCorrectText !== 'false' ? prevOptions.find((opt) => opt.is_correct)?.correct_reason || '' : '',
            incorrect_reason: existingCorrectText === 'false' ? prevOptions.find((opt) => opt.text?.trim().toLowerCase() === 'true')?.incorrect_reason || '' : '',
          },
          {
            text: 'False',
            is_correct: existingCorrectText === 'false',
            correct_reason: existingCorrectText === 'false' ? prevOptions.find((opt) => opt.is_correct)?.correct_reason || '' : '',
            incorrect_reason: existingCorrectText !== 'false' ? prevOptions.find((opt) => opt.text?.trim().toLowerCase() === 'false')?.incorrect_reason || '' : '',
          },
        ];
      });
      const hasPerOptionIncorrect = (question.options || []).some(
        (o: any) => typeof o === 'object' && o?.incorrect_reason
      );
      setTopLevelIncorrectReasoning(
        hasPerOptionIncorrect ? '' : question.incorrect_reasoning || ''
      );
      // props take priority; fall back to fields on question object
      setBoard(propBoard || question?.board || question?.board_name || '');
      setCls(propClass   || question?.class || question?.class_name || '');
      setSubject(propSubject || question?.subject || question?.subject_name || '');
      setFieldErrors({});
    }
  }, [open, question, propBoard, propClass, propSubject]);

  // ── Cascade resets ───────────────────────────────────────────────────────

  const handleBoardSelect = (val: string) => {
    setBoard(val === board ? '' : val);
    setCls(''); setSubject(''); setTopic(''); setSubtopic(''); setSelectedPrerequisites([]);
    setFieldErrors((e) => ({ ...e, board: undefined, cls: undefined, subject: undefined, topic: undefined, subtopic: undefined }));
  };

  const handleClassSelect = (val: string) => {
    setCls(val === cls ? '' : val);
    setSubject(''); setTopic(''); setSubtopic(''); setSelectedPrerequisites([]);
    setFieldErrors((e) => ({ ...e, cls: undefined, subject: undefined, topic: undefined, subtopic: undefined }));
  };

  const handleSubjectSelect = (val: string) => {
    setSubject(val === subject ? '' : val);
    setTopic(''); setSubtopic(''); setSelectedPrerequisites([]);
    setFieldErrors((e) => ({ ...e, subject: undefined, topic: undefined, subtopic: undefined }));
  };

  const handleTopicChange = (newTopic: string) => {
    setTopic(newTopic); setSubtopic(''); setSelectedPrerequisites([]);
    setFieldErrors((e) => ({ ...e, topic: undefined, subtopic: undefined }));
  };

  const handleSubtopicChange = (newSubtopic: string) => {
    setSubtopic(newSubtopic); setSelectedPrerequisites([]);
    setFieldErrors((e) => ({ ...e, subtopic: undefined }));
  };

  // ── Option handlers ──────────────────────────────────────────────────────

  const handleOptionTextChange = (idx: number, val: string) =>
    setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, text: val } : o)));

  const handleCorrectChange = (idx: number) => {
    const normalizedType = normalizeQuestionType(questionType);
    setOptions((prev) => prev.map((o, i) => ({
      ...o,
      is_correct: normalizedType === 'multiple-selection' ? (i === idx ? !o.is_correct : !!o.is_correct) : i === idx,
    })));
    setFieldErrors((e) => ({ ...e, correctOption: undefined }));
  };

  const handleQuestionTypeChange = (newType: string) => {
    const normalizedType = normalizeQuestionType(newType);
    setQuestionType(normalizedType);
    setFieldErrors((er) => ({ ...er, questionType: undefined, options: undefined, correctOption: undefined }));
    setOptions((prev) => {
      if (normalizedType === 'boolean') {
        const correctText = prev.find((opt) => opt.is_correct)?.text?.trim().toLowerCase();
        return [
          { text: 'True', is_correct: correctText !== 'false', correct_reason: correctText !== 'false' ? prev.find((opt) => opt.is_correct)?.correct_reason || '' : '', incorrect_reason: correctText === 'false' ? prev.find((opt) => opt.text?.trim().toLowerCase() === 'true')?.incorrect_reason || '' : '' },
          { text: 'False', is_correct: correctText === 'false', correct_reason: correctText === 'false' ? prev.find((opt) => opt.is_correct)?.correct_reason || '' : '', incorrect_reason: correctText !== 'false' ? prev.find((opt) => opt.text?.trim().toLowerCase() === 'false')?.incorrect_reason || '' : '' },
        ];
      }
      if (prev.length >= 4) return prev;
      return [...prev, ...getDefaultOptionsForType(normalizedType).slice(prev.length)];
    });
  };

  const handleCorrectReasonChange = (idx: number, val: string) =>
    setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, correct_reason: val } : o)));

  const handleIncorrectReasonChange = (idx: number, val: string) =>
    setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, incorrect_reason: val } : o)));

  // ── Validation ───────────────────────────────────────────────────────────

  const validateForm = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (!questionText.trim()) errors.questionText = 'Question text is required';
    if (isCreateMode) {
      if (!board.trim())   errors.board   = 'Board is required';
      if (!cls.trim())     errors.cls     = 'Class is required';
      if (!subject.trim()) errors.subject = 'Subject is required';
    }
    if (!topic.trim())    errors.topic       = 'Topic is required';
    if (!subtopic.trim()) errors.subtopic    = 'Subtopic is required';
    if (!difficulty)      errors.difficulty  = 'Difficulty is required';
    if (!questionType)    errors.questionType = 'Question type is required';
    const normalizedType = normalizeQuestionType(questionType);
    if (normalizedType === 'boolean' && options.length !== 2) {
      errors.options = 'Boolean questions must have True and False options';
    } else if (normalizedType !== 'boolean' && options.length < 4) {
      errors.options = 'Minimum 4 options are required';
    }
    for (let i = 0; i < options.length; i++) {
      if (!options[i].text.trim()) {
        errors.options = `Option ${String.fromCharCode(65 + i)} text is required`;
        break;
      }
    }
    const correctOptions = options.filter((o) => o.is_correct);
    if (normalizedType === 'multiple-selection') {
      if (correctOptions.length < 2) {
        errors.correctOption = 'Please mark at least two options as correct';
      }
    } else if (correctOptions.length !== 1) {
      errors.correctOption = 'Please mark one option as correct';
    }
    if (correctOptions.length > 0 && correctOptions.some((o) => !o.correct_reason?.trim())) {
      errors.correctReason = 'Explain why each correct answer is correct';
    }
    const incorrectWithoutReason = options.some((o) => !o.is_correct && !o.incorrect_reason?.trim());
    if (incorrectWithoutReason) errors.incorrectReasons = 'All incorrect options need an explanation';
    return errors;
  };

  // ── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }

    const trimmedQuestion = questionText.trim();
    const cleanedOptions = options
      .map((opt) => ({ ...opt, text: (opt.text || '').trim() }))
      .filter((opt) => !!opt.text);
    const normalizedType = normalizeQuestionType(questionType);
    const correctIndexes = cleanedOptions
      .map((opt, idx) => (opt.is_correct ? idx : -1))
      .filter((idx) => idx !== -1);
    if (correctIndexes.length === 0) { setFieldErrors({ correctOption: 'Please mark a correct answer' }); return; }

    const normalizedOptions = cleanedOptions.map((opt, idx) => ({
      text: opt.text,
      is_correct: normalizedType === 'multiple-selection' ? !!opt.is_correct : idx === correctIndexes[0],
      ...((normalizedType === 'multiple-selection' ? !!opt.is_correct : idx === correctIndexes[0])
        ? { correct_reason: opt.correct_reason || '' }
        : { incorrect_reason: opt.incorrect_reason || '' }),
    }));
    const correctAnswer = normalizedType === 'multiple-selection'
      ? normalizedOptions.filter((opt) => opt.is_correct).map((opt) => opt.text)
      : normalizedOptions.find((opt) => opt.is_correct)?.text || '';

    try {
      setSaving(true);
      setFieldErrors({});

      if (isCreateMode) {
        const questionData = {
          question_text: trimmedQuestion,
          topic: topic.trim(), subtopic: subtopic.trim(), difficulty,
          question_type: normalizedType, options: normalizedOptions,
          correct_answer: correctAnswer, prerequisites: selectedPrerequisites,
        };
        const payload = {
          board: board.trim(), class: cls.trim(), subject: subject.trim(), data: [questionData],
        };
        const response = await actions.createQuestionBank(payload);
        const created = response?.data?.list?.[0] || response?.data?.[0] || {
          ...questionData, board: board.trim(), class: cls.trim(), subject: subject.trim(),
          question_id: Date.now().toString(),
        };
        onSave(created);
      } else {
        const payload = {
          questions_to_update: [{
            question_id: question.question_id,
            question_bank_id: question.question_bank_id,
            company_id: question.company_id,
            difficulty, question_text: trimmedQuestion,
            options: normalizedOptions, correct_answer: correctAnswer,
            prerequisites: selectedPrerequisites, verified: true,
          }],
        };
        await actions.updateQuestions(payload);
        onSave({
          ...question,
          question_text: trimmedQuestion, question: trimmedQuestion,
          options: normalizedOptions, correct_answer: correctAnswer,
          topic: topic.trim(), subtopic: subtopic.trim(),
          difficulty, question_type: normalizedType,
        });
      }
      onClose();
    } catch (err: any) {
      setFieldErrors({ questionText: err?.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  // ── UI helpers ────────────────────────────────────────────────────────────

  const fieldLabel = (label: string, required = false) => (
    <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
      {label}{required && <span style={{ color: '#d32f2f' }}> *</span>}
    </Typography>
  );

  const ChipRow = ({
    items, selected, onSelect, emptyText = 'No options available', error,
  }: {
    items: string[]; selected: string; onSelect: (val: string) => void;
    emptyText?: string; error?: string;
  }) => (
    <Box>
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.5, minHeight: 34,
        p: 0.8, borderRadius: 1, border: '1px solid',
        borderColor: error ? '#d32f2f' : 'transparent',
        bgcolor: error ? '#fff5f5' : 'transparent',
      }}>
        {items.length === 0 ? (
          <Typography sx={{ fontSize: 11, color: '#aaa', fontStyle: 'italic', lineHeight: '26px' }}>
            {emptyText}
          </Typography>
        ) : (
          items.map((item, idx) => {
            const isSelected = selected === item;
            const color = CHIP_COLORS[idx % CHIP_COLORS.length];
            return (
              <Chip
                key={item} label={item} size="small"
                onClick={() => onSelect(item)}
                icon={isSelected
                  ? <CheckCircleIcon sx={{ fontSize: 13, color: '#fff !important' }} />
                  : undefined}
                sx={{
                  cursor: 'pointer', fontSize: 11, height: 28,
                  bgcolor: isSelected ? color : 'transparent',
                  color: isSelected ? '#fff' : '#444',
                  border: `1px solid ${color}`,
                  fontWeight: isSelected ? 600 : 400,
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: isSelected ? color : `${color}22` },
                }}
              />
            );
          })
        )}
      </Box>
      {error && <FormHelperText error sx={{ mx: 0, mt: 0.3 }}>{error}</FormHelperText>}
    </Box>
  );

  const hasPerOptionReasons = options.some(
    (o) => o.incorrect_reason || (!o.is_correct && o.incorrect_reason !== undefined)
  );
  const correctIndex = options.findIndex((o) => o.is_correct);

  // ── BODY ──────────────────────────────────────────────────────────────────

  const modalBody = (
    <>
      {/* ── Board / Class / Subject ── */}
      {isCreateMode ? (
        <Box sx={{
          p: 1.5, mb: 2, borderRadius: 2,
          border: `1px solid ${(fieldErrors.board || fieldErrors.cls || fieldErrors.subject) ? '#d32f2f' : '#E3F2FD'}`,
          bgcolor: '#F8FBFF',
        }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1565C0', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Question Context
          </Typography>

          <Box mb={1.5}>
            {fieldLabel('Board', true)}
            <ChipRow
              items={allBoards} selected={board} onSelect={handleBoardSelect}
              emptyText="No boards found in knowledge map" error={fieldErrors.board}
            />
          </Box>

          {board && (
            <Box mb={1.5}>
              {fieldLabel('Class', true)}
              <ChipRow
                items={allClasses} selected={cls} onSelect={handleClassSelect}
                emptyText="No classes found" error={fieldErrors.cls}
              />
            </Box>
          )}

          {board && cls && (
            <Box>
              {fieldLabel('Subject', true)}
              <ChipRow
                items={allSubjects} selected={subject} onSelect={handleSubjectSelect}
                emptyText="No subjects found" error={fieldErrors.subject}
              />
            </Box>
          )}
        </Box>
      ) : (
        /* ── Edit mode: read-only chips using local state ── */
        (board || cls || subject) ? (
          <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #E3F2FD', bgcolor: '#F8FBFF' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1565C0', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Question Context
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1.5}>
              {[
                { label: 'Board',   value: board   },
                { label: 'Class',   value: cls     },
                { label: 'Subject', value: subject },
              ]
                .filter((item) => !!item.value)
                .map((item, idx) => {
                  const color = CHIP_COLORS[idx % CHIP_COLORS.length];
                  return (
                    <Box key={item.label} display="flex" alignItems="center" gap={0.5}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        {item.label}:
                      </Typography>
                      <Chip
                        label={item.value}
                        size="small"
                        icon={<CheckCircleIcon sx={{ fontSize: 13, color: '#fff !important' }} />}
                        sx={{
                          fontSize: 11, height: 26,
                          bgcolor: color, color: '#fff',
                          border: `1px solid ${color}`,
                          fontWeight: 600,
                          cursor: 'default',
                          pointerEvents: 'none',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    </Box>
                  );
                })}
            </Box>
          </Box>
        ) : null
      )}

      {/* ── Question Text ── */}
      {fieldLabel('Question Text', true)}
      <TextField
        fullWidth multiline minRows={3}
        value={questionText}
        onChange={(e) => {
          setQuestionText(e.target.value);
          setFieldErrors((er) => ({ ...er, questionText: undefined }));
        }}
        size="small" sx={{ mb: 2 }}
        error={!!fieldErrors.questionText}
        helperText={fieldErrors.questionText}
      />

      {/* ── Topic — after subject in create, always in edit ── */} 
      {/* ── Topic ── */}
{(!isCreateMode || subject) && (
  <Box mb={2}>
    {fieldLabel('Topic', true)}
    {allTopics.length > 0 || !isCreateMode ? (
      <Select
        fullWidth size="small" value={topic}
        onChange={(e) => handleTopicChange(e.target.value)}
        displayEmpty error={!!fieldErrors.topic}
      >
        <MenuItem value=""><em style={{ color: '#999', fontSize: 12 }}>Select topic</em></MenuItem>
        {allTopics.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 12 }}>{t}</MenuItem>)}
      </Select>
    ) : (
      <TextField
        fullWidth size="small" value={topic}
        onChange={(e) => handleTopicChange(e.target.value)}
        placeholder="e.g. Motion"
        error={!!fieldErrors.topic}
      />
    )}
    {fieldErrors.topic && <FormHelperText error sx={{ mx: 0, mt: 0.3 }}>{fieldErrors.topic}</FormHelperText>}
  </Box>
)}

{/* ── Subtopic ── */}
{(!isCreateMode || subject) && topic && (
  <Box mb={2}>
    {fieldLabel('Subtopic', true)}
    {subtopicOptions.length > 0 || !isCreateMode ? (
      <Select
        fullWidth size="small" value={subtopic}
        onChange={(e) => handleSubtopicChange(e.target.value)}
        displayEmpty error={!!fieldErrors.subtopic}
      >
        <MenuItem value=""><em style={{ color: '#999', fontSize: 12 }}>Select subtopic</em></MenuItem>
        {subtopicOptions.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>)}
      </Select>
    ) : (
      <TextField
        fullWidth size="small" value={subtopic}
        onChange={(e) => handleSubtopicChange(e.target.value)}
        placeholder="e.g. Real Numbers"
        error={!!fieldErrors.subtopic}
      />
    )}
    {fieldErrors.subtopic && <FormHelperText error sx={{ mx: 0, mt: 0.3 }}>{fieldErrors.subtopic}</FormHelperText>}
  </Box>
)}

      {/* ── Subtopic — after topic ── */}
      {(!isCreateMode || subject) && topic && (
        <Box mb={2}>
          {fieldLabel('Subtopic', true)}
          {subtopicOptions.length > 0 ? (
            <Select
              fullWidth size="small" value={subtopic}
              onChange={(e) => handleSubtopicChange(e.target.value)}
              displayEmpty error={!!fieldErrors.subtopic}
            >
              <MenuItem value=""><em style={{ color: '#999', fontSize: 12 }}>Select subtopic</em></MenuItem>
              {subtopicOptions.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>)}
            </Select>
          ) : (
            <TextField
              fullWidth size="small" value={subtopic}
              onChange={(e) => handleSubtopicChange(e.target.value)}
              placeholder="e.g. Real Numbers"
              error={!!fieldErrors.subtopic}
            />
          )}
          {fieldErrors.subtopic && <FormHelperText error sx={{ mx: 0, mt: 0.3 }}>{fieldErrors.subtopic}</FormHelperText>}
        </Box>
      )}

      {/* ── Prerequisites — after subtopic ── */}
      {(!isCreateMode || subject) && topic && subtopic && (
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <AccountTreeOutlinedIcon sx={{ fontSize: 14, color: '#5C6BC0' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              Prerequisites
              <Typography component="span" sx={{ fontSize: 10, color: '#999', fontWeight: 400, ml: 1 }}>
                (select all that apply)
              </Typography>
            </Typography>
          </Box>
          {prerequisiteOptions.length === 0 ? (
            <Typography sx={{ fontSize: 11, color: '#aaa', fontStyle: 'italic' }}>
              No prerequisites found for selected topic → subtopic
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 0.5 }}>
              {prerequisiteOptions.map((prereq, idx) => {
                const isSelected = selectedPrerequisites.includes(prereq.name);
                const color = PREREQ_COLORS[idx % PREREQ_COLORS.length];
                return (
                  <Chip
                    key={prereq.id} label={prereq.name} size="small"
                    onClick={() => setSelectedPrerequisites((prev) =>
                      isSelected ? prev.filter((p) => p !== prereq.name) : [...prev, prereq.name]
                    )}
                    icon={isSelected
                      ? <CheckCircleIcon sx={{ fontSize: 13, color: '#fff !important' }} />
                      : undefined}
                    sx={{
                      cursor: 'pointer', fontSize: 11, height: 28,
                      bgcolor: isSelected ? color : 'transparent',
                      color: isSelected ? '#fff' : '#444',
                      border: `1px solid ${color}`,
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: isSelected ? color : `${color}22` },
                    }}
                  />
                );
              })}
            </Box>
          )}
          {selectedPrerequisites.length > 0 && (
            <Box display="flex" alignItems="center" gap={0.5} mt={0.8}>
              <Typography sx={{ fontSize: 10, color: '#5C6BC0', fontWeight: 600 }}>
                {selectedPrerequisites.length} selected:
              </Typography>
              <Typography sx={{ fontSize: 10, color: '#666' }}>
                {selectedPrerequisites.join(' · ')}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ── Difficulty & Question Type ── */}
      <Box display="flex" gap={2} mb={2}>
        <Box flex={1}>
          {fieldLabel('Difficulty', true)}
          <Select
            fullWidth size="small" value={difficulty}
            onChange={(e) => { setDifficulty(e.target.value); setFieldErrors((er) => ({ ...er, difficulty: undefined })); }}
            displayEmpty error={!!fieldErrors.difficulty}
          >
            <MenuItem value=""><em style={{ color: '#999', fontSize: 12 }}>Select difficulty</em></MenuItem>
            {DIFFICULTY_OPTIONS.map((d) => (
              <MenuItem key={d} value={d} sx={{ fontSize: 12, textTransform: 'capitalize' }}>
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.difficulty && <FormHelperText error sx={{ mx: 0, mt: 0.3 }}>{fieldErrors.difficulty}</FormHelperText>}
        </Box>
        <Box flex={1}>
          {fieldLabel('Question Type', true)}
          <Select
            fullWidth size="small" value={questionType}
            onChange={(e) => handleQuestionTypeChange(e.target.value)}
            displayEmpty error={!!fieldErrors.questionType}
          >
            <MenuItem value=""><em style={{ color: '#999', fontSize: 12 }}>Select Question Type</em></MenuItem>
            {questionTypes.map((qt) => (
              <MenuItem key={qt.id} value={qt.id}>
                <Box display="flex" alignItems="center" gap={1}>
                  {qt.icon}
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{qt.label}</Typography>
                    <Typography sx={{ fontSize: 10, color: '#777' }}>{qt.description}</Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.questionType && <FormHelperText error sx={{ mx: 0, mt: 0.3 }}>{fieldErrors.questionType}</FormHelperText>}
        </Box>
      </Box>

      {/* ── Global error banner ── */}
      {Object.keys(fieldErrors).length > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          p: 1, mb: 1.5, borderRadius: 1,
          bgcolor: '#FFF3E0', border: '1px solid #FFCC80',
        }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: '#E65100' }} />
          <Typography sx={{ fontSize: 12, color: '#BF360C' }}>
            Please fill in all required fields before saving.
          </Typography>
        </Box>
      )}

      {/* ── Options header ── */}
      <Box mb={1}>
        {fieldLabel('Options & Explanations', true)}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          p: 1, mb: 1, borderRadius: 1, border: '1px solid',
          borderColor: correctIndex === -1 ? '#FFCC80' : '#A5D6A7',
          bgcolor: correctIndex === -1 ? '#FFF3E0' : '#E8F5E9',
        }}>
          {correctIndex === -1
            ? <WarningAmberIcon sx={{ fontSize: 15, color: '#E65100', flexShrink: 0 }} />
            : <CheckCircleIcon sx={{ fontSize: 15, color: '#43A047', flexShrink: 0 }} />}
          <Typography sx={{ fontSize: 11, fontWeight: 500, color: correctIndex === -1 ? '#BF360C' : '#2E7D32' }}>
            {correctIndex === -1
              ? 'No correct answer selected — click "Set correct" on the right of the correct option'
              : normalizeQuestionType(questionType) === 'multiple-selection'
                ? `${options.filter((opt) => opt.is_correct).length} options are marked as correct`
                : `Option ${String.fromCharCode(65 + correctIndex)} is marked as correct`}
          </Typography>
        </Box>
        {fieldErrors.correctOption && <FormHelperText error sx={{ mx: 0, mb: 0.5 }}>{fieldErrors.correctOption}</FormHelperText>}
        {fieldErrors.options && <FormHelperText error sx={{ mx: 0, mb: 0.5 }}>{fieldErrors.options}</FormHelperText>}
      </Box>

      {/* ── Option cards ── */}
      {options.map((opt, idx) => {
        const isCorrect = opt.is_correct;
        const letter = String.fromCharCode(65 + idx);
        const hasCorrectReasonError = isCorrect && !!fieldErrors.correctReason;
        const hasIncorrectReasonError = !isCorrect && !!fieldErrors.incorrectReasons && !opt.incorrect_reason?.trim();

        return (
          <Box
            key={idx}
            sx={{
              mb: 1.5, borderRadius: 2, border: '2px solid',
              borderColor: isCorrect ? '#43A047' : (hasIncorrectReasonError ? '#d32f2f' : '#E0E0E0'),
              bgcolor: isCorrect ? '#F1F8E9' : '#FAFAFA',
              overflow: 'hidden',
              transition: 'border-color 0.2s, background 0.2s',
            }}
          >
            {/* Header */}
            <Box
              display="flex" alignItems="center" gap={1}
              sx={{
                px: 1.5, py: 1, borderBottom: '1px solid',
                borderColor: isCorrect ? '#A5D6A7' : '#EEEEEE',
                bgcolor: isCorrect ? '#E8F5E9' : '#F5F5F5',
              }}
            >
              <Box sx={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: isCorrect ? '#43A047' : '#BDBDBD',
                color: '#fff', fontSize: 13, fontWeight: 700,
                transition: 'background 0.2s',
              }}>
                {letter}
              </Box>

              <TextField
                fullWidth size="small" variant="standard"
                value={opt.text}
                onChange={(e) => handleOptionTextChange(idx, e.target.value)}
                placeholder={`Type option ${letter} here...`}
                InputProps={{ sx: { fontSize: 13 } }}
              />

              <Box
                onClick={() => handleCorrectChange(idx)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1.4, py: 0.6, borderRadius: 5, flexShrink: 0,
                  cursor: 'pointer', border: '1.5px solid',
                  borderColor: isCorrect ? '#43A047' : '#BDBDBD',
                  bgcolor: isCorrect ? '#43A047' : '#fff',
                  color: isCorrect ? '#fff' : '#666',
                  fontSize: 11, fontWeight: 700,
                  transition: 'all 0.18s', userSelect: 'none',
                  minWidth: 100, justifyContent: 'center',
                  boxShadow: isCorrect ? '0 1px 4px rgba(67,160,71,0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
                  '&:hover': {
                    borderColor: '#43A047',
                    bgcolor: isCorrect ? '#388E3C' : '#F1F8E9',
                    color: isCorrect ? '#fff' : '#2E7D32',
                    boxShadow: '0 2px 6px rgba(67,160,71,0.25)',
                  },
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 15 }} />
                <span>{isCorrect ? 'Correct' : 'Set correct'}</span>
              </Box>
            </Box>

            {/* Explanation */}
            <Box sx={{ px: 1.5, py: 1 }}>
              {isCorrect ? (
                <>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#2E7D32', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Why this answer is correct
                  </Typography>
                  <TextField
                    fullWidth multiline minRows={2} size="small"
                    value={opt.correct_reason || ''}
                    onChange={(e) => {
                      handleCorrectReasonChange(idx, e.target.value);
                      setFieldErrors((er) => ({ ...er, correctReason: undefined }));
                    }}
                    placeholder="Explain why this option is correct..."
                    error={hasCorrectReasonError}
                    helperText={hasCorrectReasonError ? fieldErrors.correctReason : ''}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 12, bgcolor: '#fff', borderRadius: 1 } }}
                  />
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#E65100', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Why "{opt.text || `Option ${letter}`}" is wrong
                  </Typography>
                  <TextField
                    fullWidth multiline minRows={2} size="small"
                    value={opt.incorrect_reason || ''}
                    onChange={(e) => {
                      handleIncorrectReasonChange(idx, e.target.value);
                      setFieldErrors((er) => ({ ...er, incorrectReasons: undefined }));
                    }}
                    placeholder="Explain why this option is incorrect..."
                    error={hasIncorrectReasonError}
                    helperText={hasIncorrectReasonError ? 'Explanation required' : ''}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 12, bgcolor: '#fff', borderRadius: 1 } }}
                  />
                </>
              )}
            </Box>
          </Box>
        );
      })}

      {!hasPerOptionReasons && (
        <Box mt={1}>
          <Divider sx={{ mb: 1.5 }} />
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <WarningAmberIcon sx={{ fontSize: 12, color: '#E65100' }} />
            {fieldLabel('Common Mistakes / Why Others Are Wrong')}
          </Box>
          <TextField
            fullWidth multiline minRows={2} size="small"
            value={topLevelIncorrectReasoning}
            onChange={(e) => setTopLevelIncorrectReasoning(e.target.value)}
            placeholder="Explain common mistakes or why other options are wrong..."
          />
        </Box>
      )}
    </>
  );

  // ── FOOTER ────────────────────────────────────────────────────────────────

  const modalFooter = (
    <Box display="flex" justifyContent="flex-end" gap={1}>
      <Button onClick={onClose} size="small" disabled={saving}>Cancel</Button>
      <Button
        variant="contained" size="small"
        color={isCreateMode ? 'success' : 'primary'}
        onClick={handleSave} disabled={saving}
      >
        {saving
          ? isCreateMode ? 'Creating...' : 'Saving...'
          : isCreateMode ? 'Create Question' : 'Save Changes'}
      </Button>
    </Box>
  );

  return (
    <ModalForms
      show={open}
      handleClose={onClose}
      modalHeader={
        <Box display="flex" alignItems="center" gap={1}>
          {isCreateMode ? <AddCircleOutlineIcon fontSize="small" /> : <EditOutlinedIcon fontSize="small" />}
          {isCreateMode ? 'Create New Question' : 'Edit Question'}
        </Box>
      }
      modalBody={modalBody}
      modalFooter={modalFooter}
      size="md"
    />
  );
};

export default EditQuestionModal;
